import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Paper,
  TextField,
  Autocomplete,
  alpha,
  useMediaQuery,
  useTheme,
  IconButton,
} from '@mui/material';
import {
  FlightTakeoff,
  Business,
  School,
  Work,
  FamilyRestroom,
  Security,
  Support,
  CheckCircle,
  Public,
  ArrowForward,
  Verified,
  Shield,
  Lock,
  AutoAwesome,
  CloudUpload,
  TaskAlt,
  KeyboardArrowDown,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../constants';

// Hero backgrounds — matching auth page aesthetic
const HERO_BACKGROUNDS = [
  'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1920&q=90',
  'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=1920&q=90',
  'https://images.unsplash.com/photo-1538332576228-eb5b4c4de6f5?w=1920&q=90',
  'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=1920&q=90',
];

interface Country {
  id: number;
  name: string;
  iso2: string;
  iso3: string;
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated } = useAuth();

  const [currentBg, setCurrentBg] = useState(0);
  const [countries, setCountries] = useState<Country[]>([]);
  const [fromCountry, setFromCountry] = useState<Country | null>(null);
  const [toCountry, setToCountry] = useState<Country | null>(null);

  // Rotate hero backgrounds
  useEffect(() => {
    HERO_BACKGROUNDS.forEach(url => { const img = new Image(); img.src = url; });
    const interval = setInterval(() => {
      setCurrentBg(prev => (prev + 1) % HERO_BACKGROUNDS.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  // Fetch countries for the search
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/countries?limit=300`);
        const data = await res.json();
        const countriesList = data.data || data.countries || (Array.isArray(data) ? data : []);
        if (countriesList.length > 0) {
          setCountries(countriesList.sort((a: Country, b: Country) => a.name.localeCompare(b.name)));
        }
      } catch {
        // Fallback
        setCountries([
          { id: 1, name: 'United States', iso2: 'US', iso3: 'USA' },
          { id: 2, name: 'United Kingdom', iso2: 'GB', iso3: 'GBR' },
          { id: 3, name: 'Canada', iso2: 'CA', iso3: 'CAN' },
          { id: 4, name: 'Germany', iso2: 'DE', iso3: 'DEU' },
          { id: 5, name: 'France', iso2: 'FR', iso3: 'FRA' },
          { id: 6, name: 'Australia', iso2: 'AU', iso3: 'AUS' },
          { id: 7, name: 'Japan', iso2: 'JP', iso3: 'JPN' },
          { id: 8, name: 'India', iso2: 'IN', iso3: 'IND' },
        ]);
      }
    };
    fetchCountries();
  }, []);

  const handleVisaSearch = () => {
    if (fromCountry && toCountry) {
      navigate(`/visa-check?from=${fromCountry.iso2}&to=${toCountry.iso2}`);
    } else {
      navigate('/visa-check');
    }
  };

  const features = [
    {
      icon: <AutoAwesome sx={{ fontSize: 36 }} />,
      title: 'Guided Preparation',
      description: 'Country requirements, document checklists, interview preparation, and application tracking in one workspace.',
      color: '#0EA5E9',
    },
    {
      icon: <Security sx={{ fontSize: 36 }} />,
      title: 'Access Controls',
      description: 'Authenticated and role-restricted routes protect account, application, document, and administrative data.',
      color: '#10B981',
    },
    {
      icon: <Public sx={{ fontSize: 36 }} />,
      title: '249 destinations',
      description: 'A broad country catalog with visa information, requirements, and source records where available.',
      color: '#8B5CF6',
    },
    {
      icon: <Support sx={{ fontSize: 36 }} />,
      title: 'Support Workflows',
      description: 'Submit support requests from the platform. Response times and dedicated coverage depend on your service agreement.',
      color: '#F59E0B',
    },
  ];

  const visaTypes = [
    { icon: <FlightTakeoff />, name: 'Tourist Visa', description: 'Travel & leisure', color: '#0EA5E9' },
    { icon: <Business />, name: 'Business Visa', description: 'Commercial travel', color: '#8B5CF6' },
    { icon: <School />, name: 'Student Visa', description: 'Education abroad', color: '#10B981' },
    { icon: <Work />, name: 'Work Visa', description: 'Employment permits', color: '#F59E0B' },
    { icon: <FamilyRestroom />, name: 'Family Visa', description: 'Reunification', color: '#EF4444' },
  ];

  const howItWorks = [
    {
      step: '01',
      icon: <Public sx={{ fontSize: 32 }} />,
      title: 'Select Destination',
      description: 'Choose your nationality and destination country. Our system instantly checks visa requirements and eligibility.',
    },
    {
      step: '02',
      icon: <CloudUpload sx={{ fontSize: 32 }} />,
      title: 'Upload Documents',
      description: 'Store application documents in the private document workflow and use the checklist to review completeness.',
    },
    {
      step: '03',
      icon: <TaskAlt sx={{ fontSize: 32 }} />,
      title: 'Track Your Preparation',
      description: 'Follow your Tripnovac application record and document progress. Government decisions remain with the relevant authority.',
    },
  ];

  return (
    <Box>
      {/* ═══════════════════════════════════════════════════════════
          HERO SECTION — Dark, immersive with rotating city backgrounds
          ═══════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: '90vh', md: '85vh' },
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          mt: '-80px',
          pt: '80px',
        }}
      >
        {/* Background layers */}
        {HERO_BACKGROUNDS.map((url, idx) => (
          <Box
            key={idx}
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url(${url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              transition: 'opacity 1.5s ease-in-out',
              opacity: currentBg === idx ? 1 : 0,
            }}
          />
        ))}
        {/* Dark overlay */}
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 1,
          background: 'linear-gradient(135deg, rgba(3,11,27,0.88) 0%, rgba(18,115,229,0.4) 50%, rgba(3,11,27,0.85) 100%)',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              {/* Brand logo */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box
                  component="img"
                  src="/assets/app-icon.png"
                  alt="Tripnovac"
                  sx={{
                    height: { xs: 48, md: 60 },
                    width: 'auto',
                    filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))',
                  }}
                  onError={(e: any) => { e.target.style.display = 'none'; }}
                />
                <Box
                  component="img"
                  src="/assets/nametag.png"
                  alt="Tripnovac Visa"
                  sx={{
                    height: { xs: 28, md: 36 },
                    width: 'auto',
                    filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))',
                  }}
                  onError={(e: any) => { e.target.style.display = 'none'; }}
                />
              </Box>

              {/* Trust chips */}
              <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                {[
                  { icon: <Verified sx={{ fontSize: 14 }} />, text: 'HTTPS Delivery' },
                  { icon: <Shield sx={{ fontSize: 14 }} />, text: 'Role-Restricted Access' },
                  { icon: <Lock sx={{ fontSize: 14 }} />, text: 'Stripe Card Processing' },
                ].map((badge, i) => (
                  <Chip key={i} icon={badge.icon} label={badge.text} size="small"
                    sx={{
                      bgcolor: alpha('#10B981', 0.15), color: '#6EE7B7',
                      border: `1px solid ${alpha('#10B981', 0.3)}`,
                      fontWeight: 500, fontSize: '0.7rem',
                      '& .MuiChip-icon': { color: '#10B981' },
                    }}
                  />
                ))}
              </Box>

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '3.75rem' },
                  fontWeight: 800,
                  color: 'white',
                  lineHeight: 1.1,
                  mb: 2.5,
                  letterSpacing: '-0.02em',
                }}
              >
                Global Visa Processing,{' '}
                <Box component="span" sx={{
                  background: 'linear-gradient(135deg, #0EA5E9, #14B8A6)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  Simplified
                </Box>
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: '1.05rem', md: '1.2rem' },
                  color: 'rgba(255,255,255,0.75)',
                  lineHeight: 1.7,
                  mb: 4,
                  maxWidth: 560,
                }}
              >
                Visa research, application preparation, document organization, and account workflows
                for individuals, agents, and organizations across a 250-country catalog.
              </Typography>

              {/* CTA Buttons */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate(isAuthenticated ? '/dashboard' : '/register')}
                  sx={{
                    background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)',
                    color: 'white',
                    px: 4, py: 1.5,
                    fontSize: '1.05rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(14, 165, 233, 0.35)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0284C7 0%, #0D9488 100%)',
                      boxShadow: '0 12px 40px rgba(14, 165, 233, 0.5)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {isAuthenticated ? 'Go to Dashboard' : 'Start Free Application'}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/visa-check')}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.3)',
                    color: 'white',
                    px: 4, py: 1.5,
                    fontSize: '1.05rem',
                    fontWeight: 500,
                    borderRadius: 2,
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      borderColor: 'rgba(255,255,255,0.6)',
                      bgcolor: 'rgba(255,255,255,0.08)',
                    },
                  }}
                >
                  Check Visa Requirements
                </Button>
              </Box>

              {/* Mini trust stats */}
              <Box sx={{ display: 'flex', gap: { xs: 3, md: 5 }, flexWrap: 'wrap' }}>
                {[
                  { value: '250', label: 'Country Records' },
                  { value: 'Database', label: 'Persistent Applications' },
                  { value: 'Role-Based', label: 'Account Access' },
                ].map((stat, i) => (
                  <Box key={i}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: 'white' }}>{stat.value}</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>{stat.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Right side — Visa Search Card */}
            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  background: 'linear-gradient(145deg, rgba(26,31,60,0.95) 0%, rgba(13,16,37,0.95) 100%)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: 3,
                  p: { xs: 3, md: 4 },
                  border: '1px solid rgba(255,255,255,0.08)',
                  boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Box
                    component="img"
                    src="/assets/visa-icon.png"
                    alt=""
                    sx={{ width: 24, height: 24 }}
                    onError={(e: any) => { e.target.style.display = 'none'; }}
                  />
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1.1rem' }}>
                    Instant Visa Check
                  </Typography>
                </Box>

                <Autocomplete
                  options={countries}
                  getOptionLabel={(o) => o.name}
                  value={fromCountry}
                  onChange={(_, v) => setFromCountry(v)}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img src={`https://flagcdn.com/16x12/${option.iso2.toLowerCase()}.png`}
                          alt="" style={{ width: 16, height: 12, borderRadius: 1 }}
                          onError={(e: any) => { e.target.style.display = 'none'; }} />
                        <Typography variant="body2">{option.name}</Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Your Nationality" size="small" sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2, color: 'white',
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
                        '&.Mui-focused fieldset': { borderColor: '#0EA5E9' },
                      },
                      '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.4)' },
                      '& .MuiInputBase-input': { color: 'white' },
                      '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.4)' },
                    }} />
                  )}
                />

                <Autocomplete
                  options={countries}
                  getOptionLabel={(o) => o.name}
                  value={toCountry}
                  onChange={(_, v) => setToCountry(v)}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img src={`https://flagcdn.com/16x12/${option.iso2.toLowerCase()}.png`}
                          alt="" style={{ width: 16, height: 12, borderRadius: 1 }}
                          onError={(e: any) => { e.target.style.display = 'none'; }} />
                        <Typography variant="body2">{option.name}</Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Destination Country" size="small" sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2, color: 'white',
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
                        '&.Mui-focused fieldset': { borderColor: '#0EA5E9' },
                      },
                      '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.4)' },
                      '& .MuiInputBase-input': { color: 'white' },
                      '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.4)' },
                    }} />
                  )}
                />

                <Button
                  fullWidth variant="contained" size="large"
                  onClick={handleVisaSearch}
                  endIcon={<ArrowForward />}
                  sx={{
                    py: 1.5, borderRadius: 2, fontWeight: 700, fontSize: '1rem',
                    background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)',
                    boxShadow: '0 8px 24px rgba(14,165,233,0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0284C7 0%, #0D9488 100%)',
                      boxShadow: '0 12px 32px rgba(14,165,233,0.5)',
                    },
                  }}
                >
                  Check Requirements
                </Button>

                {/* Quick stat row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2.5, pt: 2.5, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                  {[
                    { val: '< 2 min', label: 'Avg Check Time' },
                    { val: '2,051', label: 'Visa Types' },
                    { val: 'Free', label: 'Requirement Check' },
                  ].map((s, i) => (
                    <Box key={i} sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0EA5E9', fontSize: '0.85rem' }}>{s.val}</Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem' }}>{s.label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>

        {/* Scroll indicator */}
        <Box sx={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 2, textAlign: 'center' }}>
          <IconButton sx={{ color: 'rgba(255,255,255,0.5)', animation: 'bounce 2s infinite' }}>
            <KeyboardArrowDown />
          </IconButton>
        </Box>

        <style>{`@keyframes bounce { 0%, 20%, 50%, 80%, 100% { transform: translateY(0); } 40% { transform: translateY(-8px); } 60% { transform: translateY(-4px); } }`}</style>
      </Box>

      {/* ═══════════════════════════════════════════════════════════
          VERIFIED PLATFORM CHARACTERISTICS
          ═══════════════════════════════════════════════════════════ */}
      <Box sx={{ bgcolor: '#030B1B', py: { xs: 6, md: 8 }, borderBottom: '1px solid', borderColor: 'rgba(255,255,255,0.06)' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {[
              { value: '250', label: 'Country Records', icon: <Public sx={{ fontSize: 28 }} />, color: '#0EA5E9' },
              { value: 'Persistent', label: 'Application Records', icon: <TaskAlt sx={{ fontSize: 28 }} />, color: '#8B5CF6' },
              { value: 'Role-Based', label: 'Protected Access', icon: <Security sx={{ fontSize: 28 }} />, color: '#10B981' },
              { value: 'Stripe', label: 'Card Processing', icon: <CheckCircle sx={{ fontSize: 28 }} />, color: '#F59E0B' },
            ].map((stat, idx) => (
              <Grid item xs={6} md={3} key={idx}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{
                    width: 56, height: 56, borderRadius: 2, mx: 'auto', mb: 2,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: alpha(stat.color, 0.15), color: stat.color,
                  }}>
                    {stat.icon}
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 800, color: 'white', mb: 0.5 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }} fontWeight={500}>
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════
          PLATFORM BRAND SHOWCASE
          ═══════════════════════════════════════════════════════════ */}
      <Box sx={{
        py: { xs: 6, md: 8 },
        background: 'linear-gradient(180deg, #0F172A 0%, #030B1B 100%)',
        textAlign: 'center',
      }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3, mb: 3 }}>
            <Box
              component="img"
              src="/assets/Tripnovac-brand.png"
              alt="Tripnovac Logo"
              sx={{
                height: { xs: 64, md: 80 },
                width: 'auto',
                filter: 'brightness(1.1) drop-shadow(0 4px 16px rgba(14,165,233,0.2))',
              }}
              onError={(e: any) => { e.target.style.display = 'none'; }}
            />
            <Box
              component="img"
              src="/assets/nametag.png"
              alt="Tripnovac"
              sx={{
                height: { xs: 32, md: 44 },
                width: 'auto',
                filter: 'brightness(10)',
              }}
              onError={(e: any) => { e.target.style.display = 'none'; }}
            />
          </Box>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.6)', maxWidth: 520, mx: 'auto', lineHeight: 1.8, mb: 2 }}>
            Built by <strong style={{ color: 'rgba(255,255,255,0.85)' }}>Alpha Libra LLC</strong> for structured visa research,
            preparation, document organization, and account-based workflows.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {[
              { text: 'Guided Preparation', color: '#0EA5E9' },
              { text: 'Organization Workflows', color: '#8B5CF6' },
              { text: 'Global Coverage', color: '#10B981' },
              { text: 'Support Requests', color: '#F59E0B' },
            ].map((tag, i) => (
              <Chip key={i} label={tag.text} size="small"
                sx={{ bgcolor: alpha(tag.color, 0.15), color: tag.color, fontWeight: 600, border: `1px solid ${alpha(tag.color, 0.3)}` }} />
            ))}
          </Box>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════
          HOW IT WORKS — 3-step process
          ═══════════════════════════════════════════════════════════ */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: '#0F172A' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip label="Simple Process" size="small"
              sx={{ mb: 2, bgcolor: alpha('#0EA5E9', 0.15), color: '#0EA5E9', fontWeight: 600, border: `1px solid ${alpha('#0EA5E9', 0.3)}` }} />
            <Typography variant="h2" sx={{ fontWeight: 800, color: 'white', mb: 1.5 }}>
              How Tripnovac Works
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.55)', maxWidth: 520, mx: 'auto', lineHeight: 1.7 }}>
              From application to approval — our streamlined process makes visa processing effortless
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {howItWorks.map((item, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <Box sx={{ textAlign: 'center', position: 'relative' }}>
                  {/* Step number */}
                  <Box sx={{
                    width: 72, height: 72, borderRadius: '50%', mx: 'auto', mb: 3,
                    background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', boxShadow: '0 8px 24px rgba(14,165,233,0.25)',
                  }}>
                    {item.icon}
                  </Box>
                  {/* Connector line */}
                  {idx < 2 && !isMobile && (
                    <Box sx={{
                      position: 'absolute', top: 36, left: '60%', width: '80%',
                      height: 2, bgcolor: alpha('#0EA5E9', 0.2),
                      '&::after': {
                        content: '""', position: 'absolute', right: 0, top: -3,
                        width: 8, height: 8, borderRadius: '50%', bgcolor: '#0EA5E9',
                      },
                    }} />
                  )}
                  <Typography variant="overline" sx={{ color: '#0EA5E9', fontWeight: 700, letterSpacing: 2 }}>
                    Step {item.step}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, mt: 0.5, color: 'white' }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, maxWidth: 320, mx: 'auto' }}>
                    {item.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════
          FEATURES SECTION — Premium cards
          ═══════════════════════════════════════════════════════════ */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: '#030B1B' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip label="Platform Features" size="small"
              sx={{ mb: 2, bgcolor: alpha('#8B5CF6', 0.15), color: '#8B5CF6', fontWeight: 600, border: `1px solid ${alpha('#8B5CF6', 0.3)}` }} />
            <Typography variant="h2" sx={{ fontWeight: 800, color: 'white', mb: 1.5 }}>
              Why Choose Tripnovac?
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.55)', maxWidth: 560, mx: 'auto', lineHeight: 1.7 }}>
              Enterprise-grade features designed for travelers, agencies, and corporations worldwide
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {features.map((feature, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Card sx={{
                  height: '100%', border: '1px solid', borderColor: 'rgba(255,255,255,0.06)',
                  bgcolor: 'rgba(255,255,255,0.03)',
                  boxShadow: 'none', transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 20px 40px ${alpha(feature.color, 0.2)}`,
                    borderColor: alpha(feature.color, 0.4),
                    bgcolor: 'rgba(255,255,255,0.05)',
                  },
                }}>
                  <CardContent sx={{ p: 3.5, textAlign: 'center' }}>
                    <Box sx={{
                      width: 64, height: 64, borderRadius: 2, mx: 'auto', mb: 2.5,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: alpha(feature.color, 0.15), color: feature.color,
                    }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: 'white' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.7 }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════
          VISA TYPES — Interactive category cards
          ═══════════════════════════════════════════════════════════ */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: '#0F172A' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip label="2,051 Types" size="small"
              sx={{ mb: 2, bgcolor: alpha('#10B981', 0.15), color: '#10B981', fontWeight: 600, border: `1px solid ${alpha('#10B981', 0.3)}` }} />
            <Typography variant="h2" sx={{ fontWeight: 800, color: 'white', mb: 1.5 }}>
              Comprehensive Visa Coverage
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.55)', maxWidth: 520, mx: 'auto', lineHeight: 1.7 }}>
              Every visa category across 249 countries and territories (ISO 3166-1) — from tourist to business to permanent residency
            </Typography>
          </Box>

          <Grid container spacing={3} justifyContent="center">
            {visaTypes.map((type, idx) => (
              <Grid item xs={6} sm={4} md={2.4} key={idx}>
                <Paper
                  elevation={0}
                  onClick={() => navigate('/visa-check')}
                  sx={{
                    p: 3, textAlign: 'center', cursor: 'pointer',
                    border: '2px solid', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 3,
                    transition: 'all 0.3s ease', bgcolor: 'rgba(255,255,255,0.03)',
                    '&:hover': {
                      borderColor: type.color,
                      transform: 'translateY(-6px)',
                      boxShadow: `0 12px 24px ${alpha(type.color, 0.2)}`,
                      bgcolor: 'rgba(255,255,255,0.06)',
                    },
                  }}
                >
                  <Box sx={{
                    width: 52, height: 52, borderRadius: 2, mx: 'auto', mb: 2,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: alpha(type.color, 0.15), color: type.color,
                  }}>
                    {type.icon}
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: 'white' }}>
                    {type.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>
                    {type.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════
          ENTERPRISE TRUST — Compliance & security
          ═══════════════════════════════════════════════════════════ */}
      <Box sx={{
        py: { xs: 8, md: 10 },
        background: 'linear-gradient(135deg, #030B1B 0%, #0F172A 50%, #1E293B 100%)',
        color: 'white',
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip label="Enterprise Grade" size="small"
                sx={{ mb: 2, bgcolor: alpha('#0EA5E9', 0.15), color: '#0EA5E9', fontWeight: 600, border: `1px solid ${alpha('#0EA5E9', 0.3)}` }} />
              <Typography variant="h2" sx={{ fontWeight: 800, mb: 2 }}>
                Built for Scale.{' '}
                <Box component="span" sx={{
                  background: 'linear-gradient(135deg, #0EA5E9, #14B8A6)',
                  backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                }}>
                  Designed for Trust.
                </Box>
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.8, mb: 4, fontSize: '1.05rem' }}>
                Tripnovac uses authenticated workflows, role checks, private document access patterns,
                and third-party payment processing. Enterprise service levels and controls are defined by written agreement.
              </Typography>
              <Button
                variant="outlined"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/pricing')}
                sx={{
                  borderColor: 'rgba(255,255,255,0.2)', color: 'white',
                  px: 3, py: 1.2, borderRadius: 2, fontWeight: 600,
                  '&:hover': { borderColor: '#0EA5E9', bgcolor: alpha('#0EA5E9', 0.1) },
                }}
              >
                View Enterprise Plans
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                {[
                  { icon: <Shield sx={{ fontSize: 28 }} />, title: 'Role Checks', desc: 'Restricted user and administrative routes' },
                  { icon: <Verified sx={{ fontSize: 28 }} />, title: 'Audit Records', desc: 'Security-relevant activity where supported' },
                  { icon: <Lock sx={{ fontSize: 28 }} />, title: 'Private Documents', desc: 'Time-limited links when storage is configured' },
                  { icon: <Security sx={{ fontSize: 28 }} />, title: 'Payment Isolation', desc: 'Card details are handled by Stripe' },
                ].map((cert, i) => (
                  <Grid item xs={6} key={i}>
                    <Box sx={{
                      p: 2.5, borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      transition: 'all 0.3s ease',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', border: `1px solid ${alpha('#0EA5E9', 0.3)}` },
                    }}>
                      <Box sx={{ color: '#0EA5E9', mb: 1.5 }}>{cert.icon}</Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{cert.title}</Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.5, display: 'block' }}>
                        {cert.desc}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════
          CTA / CONVERSION SECTION
          ═══════════════════════════════════════════════════════════ */}
      <Box sx={{
        py: { xs: 8, md: 10 },
        background: 'linear-gradient(135deg, #1273E5 0%, #0EA5E9 50%, #14B8A6 100%)',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Decorative circles */}
        <Box sx={{
          position: 'absolute', top: -100, right: -100, width: 400, height: 400,
          borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)',
        }} />
        <Box sx={{
          position: 'absolute', bottom: -60, left: -60, width: 250, height: 250,
          borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)',
        }} />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Box
              component="img"
              src="/assets/app-icon.png"
              alt="Tripnovac"
              sx={{
                height: { xs: 48, md: 56 },
                width: 'auto',
                mb: 3,
                filter: 'brightness(10) drop-shadow(0 4px 12px rgba(0,0,0,0.2))',
              }}
              onError={(e: any) => { e.target.style.display = 'none'; }}
            />
            <Typography variant="h2" sx={{ fontWeight: 800, color: 'white', mb: 2 }}>
              Ready to Simplify Your Visa Journey?
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.85)', mb: 5, fontSize: '1.15rem', lineHeight: 1.7, maxWidth: 520, mx: 'auto' }}>
              Create an account to organize your visa research, preparation steps, and application records.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/register')}
                sx={{
                  bgcolor: 'white', color: '#1273E5',
                  px: 5, py: 1.8, fontSize: '1.1rem', fontWeight: 700,
                  borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                  '&:hover': { bgcolor: '#F8FAFC', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' },
                }}
              >
                Get Started Free
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/contact')}
                sx={{
                  borderColor: 'rgba(255,255,255,0.4)', color: 'white',
                  px: 5, py: 1.8, fontSize: '1.1rem', fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                Contact Sales
              </Button>
            </Box>

            {/* Bottom trust */}
            <Box sx={{ mt: 5, display: 'flex', justifyContent: 'center', gap: { xs: 2, md: 4 }, flexWrap: 'wrap' }}>
              {[
                { icon: <CheckCircle sx={{ fontSize: 16 }} />, text: 'No credit card required' },
                { icon: <CheckCircle sx={{ fontSize: 16 }} />, text: 'Free visa requirement checks' },
                { icon: <CheckCircle sx={{ fontSize: 16 }} />, text: 'Cancel anytime' },
              ].map((item, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ color: 'rgba(255,255,255,0.7)' }}>{item.icon}</Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                    {item.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
